import * as React from 'react';
import { useState, useEffect } from 'react';
import { UserService } from '@enginuity-common/services';
import { setCookie } from '@enginuity-common/utils/cookies';
import { isSignedIn, getStoredToken, removeStoredToken, Token, GroupsClaim } from '@enginuity-common/utils/auth';
import EngageOnboardForm, { IEngageOnboardingFormData } from '../../components/onboarding/engage-onboard-form';
import { SpinnerOverlay } from '@enginuity-apps-ui-components/atoms';
import GenericErrorMessage from '../../components/misc/generic-error-message';
import { IEngageOnboardingData } from '@enginuity-common/types/user';


export type IEngageProps = {
  fromUri: string;
};

export type IAccountToken = Token & GroupsClaim;

export default function Engage({fromUri}: IEngageProps) {
  const [token, setToken] = useState<IAccountToken | null>(null);
  const [submitError, setSubmitError] = useState<JSX.Element | undefined>();
  const [showSpinner, setShowSpinner] = useState(false);
  const [genericError, setGenericError] = useState<string | undefined>();

  useEffect(() => {
    if (!isSignedIn()) {
      setCookie('enginuity-auth-origin', window.location.pathname, 1);
      window.location.href = '/auth';
    }

    const storedToken = getStoredToken<IAccountToken>();

    if (!storedToken) {
      return;
    }

    setToken(storedToken);

    const userHasOnboarded = storedToken.groups && storedToken.groups.indexOf('Engage by Enginuity') > -1;

    if (userHasOnboarded) {
      window.location.href = window.spConfig.postActivationDestination as string;
    }
  }, []);


  const usersService = new UserService({
    baseUrl: window.spConfig.usersBaseUrl
  });

  const onSubmit = async (data: IEngageOnboardingFormData) => {
    setSubmitError(undefined);
    setShowSpinner(true);

    if (data.employmentStatus === "Unemployed") {
      data.employer = "";
    }

    const dataToSend: IEngageOnboardingData = {
      employer: data.employer,
      qualification: data.qualification?.value,
      employmentStatus: data.employmentStatus,
      ageRange: data.ageRange?.value,
      sector: data.sector?.value,
    };

    try {
      const updateUserResponse = await usersService.updateUser(dataToSend);
      if (!updateUserResponse.ok) {
        throw new Error('Unsuccessful response from updateUser call');
      }

      const assignGroupsResponse = await usersService.assignGroups([
        window.spConfig.engageOktaGroupId as string,
        window.spConfig.onboardingOktaGroupId as string
      ]);
      if (!assignGroupsResponse.ok) {
        throw new Error('Unsuccessful response from assignGroups call');
      }
    } catch (error) {
      setGenericError(error);
      setShowSpinner(false);
      return;
    }

    //delete token cookie so that next time we don't get prompted for onboarding again
    removeStoredToken();

    setShowSpinner(false);
    window.location.href = window.spConfig.postActivationDestination as string;
  };

  if (!token) {
    return <></>
  }

  if (genericError) {
    return <GenericErrorMessage errorMsg={genericError} tryAgainLink="/register" fromUri={fromUri}></GenericErrorMessage>
  }

  return (
    <div className="container  ">
      <div className="row md-spacer-80 spacer-32">
        <div className="col-lg-1"></div>
        <div className="col-lg-11">
          <h1>Update my account</h1>
        </div>
      </div>
      <EngageOnboardForm onSubmit={onSubmit} submitError={submitError} startNumber={1} />
      <SpinnerOverlay active={showSpinner} />
    </div>
  );
}
